import { PlanInterval } from "./../store/checkout/types";
import { useGetCurrentPlanQuery } from "@/store/checkout/endpoints";
import { CurrentPlanDetails } from "@/store/checkout/types";
import { useAppSelector } from "@/store/hooks";
import { useGetTenantQuery, useGetTenantsListQuery } from "@/store/tenants/endpoints";
import {
  PlanType,
  TenantDetailedType,
  TenantMembership,
  TenantType,
  TenantTypeEnum,
} from "@/store/tenants/endpoints/tenants";
import { getTenantInfo } from "@/utils/auth/tenants";
import { useMemo, useState } from "react";
import { crmVaultLicenses } from "@utils/constants/plans.ts";

export const useGetCurrentTenantInfo = () => {
  const { tenantId } = useAppSelector(state => state.tenant);
  const { data: currentTenantData } = useGetTenantQuery(tenantId as string, { skip: !tenantId });
  const { data: tenantList } = useGetTenantsListQuery();
  const { data: currentPlan } = useGetCurrentPlanQuery();

  const tenantObject = useMemo(
    () =>
      currentTenantData && tenantList
        ? ({
            ...currentTenantData,
            membership:
              tenantList?.find(tenant => tenant.id === currentTenantData?.id)?.membership ??
              TenantMembership.MEMBER,
            isCrmVaultXMember:
              tenantList?.find(tenant => tenant.id === currentTenantData?.id)?.isCrmVaultXMember ??
              false,
            plan: currentPlan
              ? {
                  ...currentPlan,
                  recurringInterval: currentPlan?.items.at(0)?.price.recurringInterval ?? null,
                }
              : {
                  items: [],
                },
          } as TenantDetailedType & {
            plan: CurrentPlanDetails & { recurringInterval: PlanInterval | null };
            isCrmVaultXMember: boolean;
          })
        : null,

    [currentPlan, currentTenantData, tenantList]
  );

  const tenantInfo = useMemo(
    () => (tenantObject ? getTenantInfo(tenantObject) : null),
    [tenantObject]
  );

  if (!tenantId || !currentTenantData || !tenantInfo) return undefined;
  return tenantInfo;
};

export const useGetPersonalTenantInfo = () => {
  const { data: tenantList } = useGetTenantsListQuery();
  const [personalTenant, setPersonalTenant] = useState<TenantType | undefined>();

  const { data: currentTenantData } = useGetTenantQuery(personalTenant?.id as string, {
    skip: !personalTenant?.id,
  });

  const { data: currentPlan } = useGetCurrentPlanQuery(
    { tenantId: personalTenant?.id },
    { skip: !personalTenant?.id }
  );

  const personal = useMemo(() => {
    const found = tenantList?.find(
      tenant =>
        tenant.membership === TenantMembership.OWNER &&
        tenant.name.includes("Personal") &&
        tenant.tenantType === TenantTypeEnum.Personal
    );
    setPersonalTenant(found);
    return found;
  }, [tenantList]);

  const personalTenantInfo = useMemo(() => {
    const planInfo =
      currentTenantData && currentPlan
        ? ({
            ...currentTenantData,
            plan: currentPlan
              ? {
                  ...currentPlan,
                  recurringInterval: currentPlan?.items.at(0)?.price.recurringInterval ?? null,
                }
              : {
                  items: [],
                },
          } as PlanInfoType)
        : null;

    return planInfo ? getPersonalTenantInfo(planInfo) : null;
  }, [currentPlan, personalTenant, currentTenantData]);

  return { personalTenant: personal, personalTenantDetailsInfo: personalTenantInfo };
};

type PlanInfoType = TenantDetailedType & {
  plan: CurrentPlanDetails & { recurringInterval: PlanInterval | null };
};

const getPersonalTenantInfo = (tenant: PlanInfoType) => {
  return {
    isCRMVault:
      tenant?.licensesInfo?.some(lic => lic.license === PlanType.CRM_VAULT) ||
      tenant?.plan?.items?.some(item => crmVaultLicenses.includes(item.price.id)),
    interval: tenant.plan.recurringInterval,
    plan: tenant.plan,
    tenant,
  };
};
